import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, ArrowLeft, Mail } from 'lucide-react';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { SEO } from '../components/SEO';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#060C18] flex" data-testid="login-page">
      <SEO title="Login" description="Sign in to your SendPI account. Access PI Check, PI Craft, Token Optimizer, and 4.3M precision casts." path="/login" />
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 md:px-16 lg:px-24">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/55 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="mb-8">
            <Link to="/" className="inline-block mb-6">
              <img src="/sendpi-icon.png" alt="SendPI" className="h-9 w-auto inline-block mr-2 align-middle" />
              <span className="font-space text-2xl font-bold text-white align-middle">Send<span className="text-[var(--pi-cyan)]">PI</span></span>
            </Link>
            <h1 className="font-space text-3xl text-white mb-2">Welcome back</h1>
            <p className="text-white/45">Sign in to your SendPI account</p>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/70">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="bg-[#0E1830] border-white/10 text-white placeholder:text-white/25 focus:border-cyan-500 focus:ring-cyan-500/20"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-white/70">Password</Label>
                <Link to="/forgot-password" className="text-sm text-cyan-400 hover:text-cyan-300">
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="bg-[#0E1830] border-white/10 text-white placeholder:text-white/25 focus:border-cyan-500 focus:ring-cyan-500/20"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full shimmer-btn text-white rounded-xl py-6 border-0"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center text-xs"><span className="px-3 bg-[#060C18] text-white/40">or</span></div>
          </div>

          <GoogleSignInButton />

          <div className="mt-8 text-center">
            <p className="text-white/55">
              Don't have an account?{' '}
              <Link to="/signup" className="text-cyan-400 hover:text-cyan-300">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-[#0A1222] border-l border-white/5">
        <div className="max-w-md text-center px-12">
          <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-cyan-500 to-purple-500 mx-auto mb-8 flex items-center justify-center">
            <Mail className="w-12 h-12 text-white" />
          </div>
          <h2 className="font-space text-2xl text-white mb-4">
            Communicate with confidence
          </h2>
          <p className="text-white/55">
            Sign in to predict how your messages land, craft powerful AI prompts, 
            and unlock your full potential with SendPI.
          </p>
        </div>
      </div>
    </div>
  );
}
